import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { NIMNY_AVATAR } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';
import { NightAmbience } from '../components/NimnyLanding';

// Privacy Policy content in all languages
const privacyContent = {
  en: {
    title: 'Privacy Policy',
    backToHome: 'Back to home',
    lastUpdated: 'Last updated: April 2026',
    intro: 'This Privacy Policy explains how Nimny LLC ("Nimny", "we", "us", or "our") collects, uses, discloses, and protects your information when you use our websites, mobile applications, and related services (the "Service").',
    disclaimer: 'If you do not agree with this Privacy Policy, please do not use the Service.',
    supportButton: 'Contact Support',
    sections: [
      {
        title: '1. Who we are',
        content: [
          'Company: Nimny LLC',
          'Websites: https://readnimny.com, https://nimny.app',
          'Nimny LLC is the data controller of your personal information.'
        ]
      },
      {
        title: '2. Information we collect',
        content: 'We collect information you provide, information collected automatically, and limited information from third parties.'
      },
      {
        title: 'A. Information you provide',
        content: [
          'You may provide:',
          '• Account information (email, login data)\n• Optional family profile (child name, age, preferences — optional; nicknames can be used)\n• Story inputs (prompts, selections, preferences)\n• Support messages and feedback\n• Email (if you subscribe to updates)'
        ]
      },
      {
        title: 'B. Voice and microphone data',
        content: [
          'If you use voice features:',
          '• Audio input (only when you actively use the feature)\n• Speech-to-text transcripts (if enabled)\n• Usage metadata (duration, feature usage, errors)',
          '👉 We do not access your microphone without your permission.',
          '👉 You can disable microphone access at any time in your device settings.',
          '👉 Voice data is:\n• used to provide the feature\n• not stored permanently unless you explicitly save it\n• deleted or anonymized when no longer needed'
        ]
      },
      {
        title: 'C. Information collected automatically',
        content: [
          'We may collect:',
          '• Device information (device type, OS, app version)\n• Usage data (pages/screens viewed, actions, time spent)\n• IP address (used for approximate location)\n• Diagnostics and crash logs\n• Cookies and local storage (for login and preferences)'
        ]
      },
      {
        title: '3. How we use information',
        content: [
          'We use information to:',
          '• Provide and operate the Service\n• Generate and deliver stories and audio\n• Personalize user experience\n• Improve performance and features\n• Provide customer support\n• Ensure security and prevent abuse\n• Communicate important updates\n• Comply with legal obligations'
        ]
      },
      {
        title: '4. How we share information',
        content: 'We do not sell your personal data. We may share information with trusted service providers.'
      },
      {
        title: 'A. Service providers',
        content: [
          'We use third-party providers for:',
          '• Hosting and infrastructure\n• Analytics and diagnostics\n• Customer support tools\n• Email delivery\n• Content and voice processing\n• Security and fraud prevention',
          'These providers process data only on our behalf.'
        ]
      },
      {
        title: 'B. Payments',
        content: [
          '• Web payments: processed by providers such as Stripe\n• In-app purchases: handled by Apple App Store or Google Play',
          '👉 We do not store full payment card details.'
        ]
      },
      {
        title: 'C. Adapty (subscriptions and analytics)',
        content: [
          'We use Adapty to manage subscriptions, paywalls, and subscription analytics.',
          'Adapty may process:\n• subscription status\n• purchase events and receipts\n• device identifiers\n• usage data related to subscriptions',
          'Adapty acts as a service provider on our behalf.',
          'More information: https://adapty.io/privacy'
        ]
      },
      {
        title: 'D. Legal and safety',
        content: 'We may disclose information if required by law or to protect users, rights, and safety.'
      },
      {
        title: 'E. Business transfers',
        content: 'If Nimny is involved in a merger, acquisition, or sale, data may be transferred.'
      },
      {
        title: 'F. Third-Party AI Providers',
        content: [
          'We use third-party AI providers, including OpenAI, Anthropic, and ElevenLabs, to power certain features of the Service, such as story generation, conversational responses, speech-to-text, and voice narration.',
          'Depending on the feature used, we may share the following data with these providers:\n• user prompts and story instructions\n• optional child name, age range, preferences, and themes, if provided by the user\n• voice recordings and transcripts for voice-enabled features',
          'We do not share the following with AI providers:\n• payment card data\n• account passwords or login credentials\n• precise location data',
          'We share only the minimum data necessary to provide the requested feature.'
        ]
      },
      {
        title: '5. AI-generated content',
        content: [
          'Some AI-powered features require limited user-provided input to be sent to third-party AI providers in order to generate stories, responses, or voice content.',
          'Some stories, messages, and audio are generated using artificial intelligence.',
          '• AI-generated content may be inaccurate or unexpected\n• It may not always be suitable for every situation',
          '👉 Content is provided for entertainment purposes only.',
          '👉 You are responsible for reviewing content for your child or family.'
        ]
      },
      {
        title: '5A. AI Notice and Consent',
        content: [
          'Before a user first uses AI-powered features, the app displays an in-app notice explaining what data may be sent to third-party AI providers and for what purpose.',
          'By tapping “Save” and continuing to use AI-powered features, the user agrees to the processing described in that notice.'
        ]
      },
      {
        title: '6. Children and family use',
        content: [
          'Nimny is designed for families.',
          '• Children must use the Service with parental supervision\n• We do not knowingly collect personal information from children under 13 without parental consent\n• Providing child-related data is optional\n• You may use nicknames instead of real names',
          'If you believe a child has provided personal data without consent, contact us.'
        ]
      },
      {
        title: '7. Data retention',
        content: [
          'We retain information only as long as necessary to:\n• provide the Service\n• maintain security\n• comply with legal obligations',
          'We typically retain data while your account is active and for a limited period thereafter.',
          'You may delete your account directly in the app by going to Account > Delete Account. You may also contact us using the contact form on our website if you need additional privacy-related assistance.'
        ]
      },
      {
        title: '8. Your rights',
        content: [
          'Depending on your location, you may have rights to:\n• Access your data\n• Correct your data\n• Delete your data\n• Restrict processing\n• Object to processing',
          'To exercise your rights, you may use the in-app Delete Account feature or contact us using the contact form on our website.'
        ]
      },
      {
        title: '9. Your choices',
        content: 'You can:\n• Disable microphone permissions\n• Edit or remove profile data\n• Unsubscribe from emails\n• Control cookies via your browser'
      },
      {
        title: '10. Security',
        content: 'We implement reasonable technical and organizational safeguards. However, no system is completely secure.'
      },
      {
        title: '11. International users',
        content: 'Data may be processed and stored in the United States or other countries.'
      },
      {
        title: '12. Legal basis (EEA/UK)',
        content: 'We process data based on:\n• Contract (providing the Service)\n• Legitimate interests (security, improvement)\n• Consent (voice features, marketing)\n• Legal obligations'
      },
      {
        title: '13. Automated decision-making',
        content: 'We do not use your data to make automated decisions that have legal or similarly significant effects.'
      },
      {
        title: '14. California privacy rights',
        content: [
          'California residents may have rights to:\n• Know what data is collected\n• Request deletion\n• Request correction',
          'We do not sell personal information.'
        ]
      },
      {
        title: '15. Changes to this policy',
        content: 'We may update this Privacy Policy. We will notify users of significant changes.'
      },
      {
        title: '16. Contact',
        content: 'For privacy-related questions, please use the form below.'
      }
    ]
  },
  ru: {
    title: 'Политика конфиденциальности',
    backToHome: 'На главную',
    lastUpdated: 'Последнее обновление: апрель 2026',
    intro: 'Настоящая Политика конфиденциальности объясняет, как Nimny LLC («Nimny», «мы», «нас» или «наш») собирает, использует, раскрывает и защищает вашу информацию при использовании наших веб-сайтов, мобильных приложений и связанных сервисов («Сервис»).',
    disclaimer: 'Если вы не согласны с данной Политикой конфиденциальности, пожалуйста, не используйте Сервис.',
    supportButton: 'Связаться со службой поддержки',
    sections: [
      {
        title: '1. Кто мы',
        content: [
          'Компания: Nimny LLC',
          'Веб-сайты: https://readnimny.com, https://nimny.app',
          'Nimny LLC является оператором ваших персональных данных.'
        ]
      },
      {
        title: '2. Какую информацию мы собираем',
        content: 'Мы собираем информацию, которую вы предоставляете, информацию, собираемую автоматически, и ограниченную информацию от третьих сторон.'
      },
      {
        title: 'A. Информация, которую вы предоставляете',
        content: [
          'Вы можете предоставить:',
          '• Данные аккаунта (email, данные входа)\n• Опциональный семейный профиль (имя ребёнка, возраст, предпочтения — опционально; можно использовать псевдонимы)\n• Данные для историй (запросы, выбор, предпочтения)\n• Сообщения в поддержку и отзывы\n• Email (при подписке на обновления)'
        ]
      },
      {
        title: 'B. Голосовые данные и микрофон',
        content: [
          'При использовании голосовых функций:',
          '• Аудиовход (только при активном использовании функции)\n• Транскрипции речи в текст (если включено)\n• Метаданные использования (длительность, использование функций, ошибки)',
          '👉 Мы не получаем доступ к вашему микрофону без вашего разрешения.',
          '👉 Вы можете отключить доступ к микрофону в любое время в настройках устройства.',
          '👉 Голосовые данные:\n• используются для работы функции\n• не хранятся постоянно, если вы явно не сохраните их\n• удаляются или анонимизируются, когда больше не нужны'
        ]
      },
      {
        title: 'C. Автоматически собираемая информация',
        content: [
          'Мы можем собирать:',
          '• Информацию об устройстве (тип устройства, ОС, версия приложения)\n• Данные об использовании (просмотренные страницы/экраны, действия, время)\n• IP-адрес (для определения примерного местоположения)\n• Диагностику и журналы сбоев\n• Cookies и локальное хранилище (для входа и настроек)'
        ]
      },
      {
        title: '3. Как мы используем информацию',
        content: [
          'Мы используем информацию для:',
          '• Предоставления и работы Сервиса\n• Генерации и доставки историй и аудио\n• Персонализации пользовательского опыта\n• Улучшения производительности и функций\n• Предоставления поддержки клиентов\n• Обеспечения безопасности и предотвращения злоупотреблений\n• Сообщения важных обновлений\n• Соблюдения правовых обязательств'
        ]
      },
      {
        title: '4. Как мы делимся информацией',
        content: 'Мы не продаём ваши персональные данные. Мы можем делиться информацией с доверенными поставщиками услуг.'
      },
      {
        title: 'A. Поставщики услуг',
        content: [
          'Мы используем сторонних поставщиков для:',
          '• Хостинга и инфраструктуры\n• Аналитики и диагностики\n• Инструментов поддержки клиентов\n• Доставки email\n• Обработки контента и голоса\n• Безопасности и предотвращения мошенничества',
          'Эти поставщики обрабатывают данные только от нашего имени.'
        ]
      },
      {
        title: 'B. Платежи',
        content: [
          '• Веб-платежи: обрабатываются такими провайдерами как Stripe\n• Покупки в приложении: обрабатываются Apple App Store или Google Play',
          '👉 Мы не храним полные данные платёжных карт.'
        ]
      },
      {
        title: 'C. Adapty (подписки и аналитика)',
        content: [
          'Мы используем Adapty для управления подписками, платёжными экранами и аналитикой подписок.',
          'Adapty может обрабатывать:\n• статус подписки\n• события покупок и чеки\n• идентификаторы устройств\n• данные использования, связанные с подписками',
          'Adapty действует как поставщик услуг от нашего имени.',
          'Подробнее: https://adapty.io/privacy'
        ]
      },
      {
        title: 'D. Правовые требования и безопасность',
        content: 'Мы можем раскрывать информацию по требованию закона или для защиты пользователей, прав и безопасности.'
      },
      {
        title: 'E. Передача бизнеса',
        content: 'Если Nimny участвует в слиянии, поглощении или продаже, данные могут быть переданы.'
      },
      {
        title: 'F. Сторонние поставщики ИИ',
        content: [
          'Мы используем сторонних поставщиков ИИ, включая OpenAI, Anthropic и ElevenLabs, для работы определённых функций Сервиса — таких как генерация историй, диалоговые ответы, преобразование речи в текст и голосовое озвучивание.',
          'В зависимости от используемой функции мы можем передавать этим поставщикам следующие данные:\n• пользовательские запросы и инструкции к историям\n• опциональные имя ребёнка, возрастной диапазон, предпочтения и темы — если предоставлены пользователем\n• аудиозаписи и транскрипции для голосовых функций',
          'Мы не передаём поставщикам ИИ следующие данные:\n• данные платёжных карт\n• пароли от аккаунта или учётные данные для входа\n• точные геоданные',
          'Мы передаём только минимально необходимые данные для предоставления запрошенной функции.'
        ]
      },
      {
        title: '5. Контент, созданный ИИ',
        content: [
          'Некоторые функции, основанные на ИИ, требуют отправки ограниченных пользовательских данных сторонним поставщикам ИИ для генерации историй, ответов или голосового контента.',
          'Некоторые истории, сообщения и аудио создаются с помощью искусственного интеллекта.',
          '• Контент, созданный ИИ, может быть неточным или неожиданным\n• Он может не всегда подходить для каждой ситуации',
          '👉 Контент предоставляется только в развлекательных целях.',
          '👉 Вы несёте ответственность за проверку контента для вашего ребёнка или семьи.'
        ]
      },
      {
        title: '5A. Уведомление и согласие на использование ИИ',
        content: [
          'Перед первым использованием функций на основе ИИ приложение показывает уведомление, в котором объясняется, какие данные могут быть отправлены сторонним поставщикам ИИ и с какой целью.',
          'Нажимая «Сохранить» и продолжая использовать функции на основе ИИ, пользователь соглашается с обработкой, описанной в этом уведомлении.'
        ]
      },
      {
        title: '6. Дети и семейное использование',
        content: [
          'Nimny создан для семей.',
          '• Дети должны использовать Сервис под присмотром родителей\n• Мы сознательно не собираем персональные данные детей младше 13 лет без согласия родителей\n• Предоставление данных о детях опционально\n• Вы можете использовать псевдонимы вместо настоящих имён',
          'Если вы считаете, что ребёнок предоставил персональные данные без согласия, свяжитесь с нами.'
        ]
      },
      {
        title: '7. Хранение данных',
        content: [
          'Мы храним информацию только столько, сколько необходимо для:\n• предоставления Сервиса\n• поддержания безопасности\n• соблюдения правовых обязательств',
          'Обычно мы храним данные, пока ваш аккаунт активен, и ограниченный период после этого.',
          'Вы можете удалить свой аккаунт прямо в приложении: «Аккаунт» → «Удалить аккаунт». Вы также можете связаться с нами через форму обратной связи на нашем сайте, если вам нужна дополнительная помощь по вопросам конфиденциальности.'
        ]
      },
      {
        title: '8. Ваши права',
        content: [
          'В зависимости от вашего местоположения, вы можете иметь права на:\n• Доступ к вашим данным\n• Исправление ваших данных\n• Удаление ваших данных\n• Ограничение обработки\n• Возражение против обработки',
          'Чтобы реализовать ваши права, вы можете воспользоваться функцией «Удалить аккаунт» в приложении или связаться с нами через форму обратной связи на нашем сайте.'
        ]
      },
      {
        title: '9. Ваш выбор',
        content: 'Вы можете:\n• Отключить разрешения на микрофон\n• Редактировать или удалять данные профиля\n• Отписаться от рассылок\n• Управлять cookies через браузер'
      },
      {
        title: '10. Безопасность',
        content: 'Мы применяем разумные технические и организационные меры защиты. Однако ни одна система не является полностью безопасной.'
      },
      {
        title: '11. Международные пользователи',
        content: 'Данные могут обрабатываться и храниться в США или других странах.'
      },
      {
        title: '12. Правовые основания (ЕЭЗ/Великобритания)',
        content: 'Мы обрабатываем данные на основании:\n• Договора (предоставление Сервиса)\n• Законных интересов (безопасность, улучшение)\n• Согласия (голосовые функции, маркетинг)\n• Правовых обязательств'
      },
      {
        title: '13. Автоматизированное принятие решений',
        content: 'Мы не используем ваши данные для принятия автоматизированных решений, имеющих юридические или аналогичные значимые последствия.'
      },
      {
        title: '14. Права жителей Калифорнии',
        content: [
          'Жители Калифорнии могут иметь права на:\n• Получение информации о собираемых данных\n• Запрос на удаление\n• Запрос на исправление',
          'Мы не продаём персональную информацию.'
        ]
      },
      {
        title: '15. Изменения в политике',
        content: 'Мы можем обновлять эту Политику конфиденциальности. Мы уведомим пользователей о значительных изменениях.'
      },
      {
        title: '16. Контакты',
        content: 'По вопросам конфиденциальности воспользуйтесь, пожалуйста, формой ниже.'
      }
    ]
  },
  es: {
    title: 'Política de Privacidad',
    backToHome: 'Volver al inicio',
    lastUpdated: 'Última actualización: abril 2026',
    intro: 'Esta Política de Privacidad explica cómo Nimny LLC ("Nimny", "nosotros", "nos" o "nuestro") recopila, usa, divulga y protege tu información cuando utilizas nuestros sitios web, aplicaciones móviles y servicios relacionados (el "Servicio").',
    disclaimer: 'Si no estás de acuerdo con esta Política de Privacidad, por favor no uses el Servicio.',
    supportButton: 'Contactar con soporte',
    sections: [
      {
        title: '1. Quiénes somos',
        content: [
          'Empresa: Nimny LLC',
          'Sitios web: https://readnimny.com, https://nimny.app',
          'Nimny LLC es el responsable del tratamiento de tu información personal.'
        ]
      },
      {
        title: '2. Información que recopilamos',
        content: 'Recopilamos información que proporcionas, información recopilada automáticamente e información limitada de terceros.'
      },
      {
        title: 'A. Información que proporcionas',
        content: [
          'Puedes proporcionar:',
          '• Información de cuenta (email, datos de inicio de sesión)\n• Perfil familiar opcional (nombre del niño, edad, preferencias — opcional; se pueden usar apodos)\n• Entradas para historias (indicaciones, selecciones, preferencias)\n• Mensajes de soporte y comentarios\n• Email (si te suscribes a actualizaciones)'
        ]
      },
      {
        title: 'B. Datos de voz y micrófono',
        content: [
          'Si usas funciones de voz:',
          '• Entrada de audio (solo cuando usas activamente la función)\n• Transcripciones de voz a texto (si está habilitado)\n• Metadatos de uso (duración, uso de funciones, errores)',
          '👉 No accedemos a tu micrófono sin tu permiso.',
          '👉 Puedes desactivar el acceso al micrófono en cualquier momento en la configuración de tu dispositivo.',
          '👉 Los datos de voz:\n• se usan para proporcionar la función\n• no se almacenan permanentemente a menos que los guardes explícitamente\n• se eliminan o anonimizan cuando ya no son necesarios'
        ]
      },
      {
        title: 'C. Información recopilada automáticamente',
        content: [
          'Podemos recopilar:',
          '• Información del dispositivo (tipo de dispositivo, SO, versión de la app)\n• Datos de uso (páginas/pantallas vistas, acciones, tiempo)\n• Dirección IP (para ubicación aproximada)\n• Diagnósticos y registros de fallos\n• Cookies y almacenamiento local (para inicio de sesión y preferencias)'
        ]
      },
      {
        title: '3. Cómo usamos la información',
        content: [
          'Usamos la información para:',
          '• Proporcionar y operar el Servicio\n• Generar y entregar historias y audio\n• Personalizar la experiencia del usuario\n• Mejorar el rendimiento y las funciones\n• Proporcionar soporte al cliente\n• Garantizar la seguridad y prevenir abusos\n• Comunicar actualizaciones importantes\n• Cumplir con obligaciones legales'
        ]
      },
      {
        title: '4. Cómo compartimos información',
        content: 'No vendemos tus datos personales. Podemos compartir información con proveedores de servicios de confianza.'
      },
      {
        title: 'A. Proveedores de servicios',
        content: [
          'Usamos proveedores externos para:',
          '• Alojamiento e infraestructura\n• Análisis y diagnósticos\n• Herramientas de soporte al cliente\n• Entrega de emails\n• Procesamiento de contenido y voz\n• Seguridad y prevención de fraude',
          'Estos proveedores procesan datos solo en nuestro nombre.'
        ]
      },
      {
        title: 'B. Pagos',
        content: [
          '• Pagos web: procesados por proveedores como Stripe\n• Compras en la app: gestionadas por Apple App Store o Google Play',
          '👉 No almacenamos los datos completos de tarjetas de pago.'
        ]
      },
      {
        title: 'C. Adapty (suscripciones y análisis)',
        content: [
          'Usamos Adapty para gestionar suscripciones, paywalls y análisis de suscripciones.',
          'Adapty puede procesar:\n• estado de suscripción\n• eventos de compra y recibos\n• identificadores de dispositivo\n• datos de uso relacionados con suscripciones',
          'Adapty actúa como proveedor de servicios en nuestro nombre.',
          'Más información: https://adapty.io/privacy'
        ]
      },
      {
        title: 'D. Legal y seguridad',
        content: 'Podemos divulgar información si lo requiere la ley o para proteger usuarios, derechos y seguridad.'
      },
      {
        title: 'E. Transferencias comerciales',
        content: 'Si Nimny participa en una fusión, adquisición o venta, los datos pueden ser transferidos.'
      },
      {
        title: 'F. Proveedores externos de IA',
        content: [
          'Usamos proveedores externos de IA, incluidos OpenAI, Anthropic y ElevenLabs, para alimentar ciertas funciones del Servicio, como la generación de historias, respuestas conversacionales, conversión de voz a texto y narración por voz.',
          'Según la función utilizada, podemos compartir los siguientes datos con estos proveedores:\n• indicaciones del usuario e instrucciones para la historia\n• nombre opcional del niño, rango de edad, preferencias y temas, si los proporciona el usuario\n• grabaciones de voz y transcripciones para funciones habilitadas por voz',
          'No compartimos lo siguiente con los proveedores de IA:\n• datos de tarjetas de pago\n• contraseñas de cuenta o credenciales de inicio de sesión\n• datos de ubicación precisos',
          'Compartimos solo los datos mínimos necesarios para proporcionar la función solicitada.'
        ]
      },
      {
        title: '5. Contenido generado por IA',
        content: [
          'Algunas funciones basadas en IA requieren que se envíen datos limitados proporcionados por el usuario a proveedores externos de IA para generar historias, respuestas o contenido de voz.',
          'Algunas historias, mensajes y audio se generan usando inteligencia artificial.',
          '• El contenido generado por IA puede ser inexacto o inesperado\n• Puede no ser siempre adecuado para cada situación',
          '👉 El contenido se proporciona solo con fines de entretenimiento.',
          '👉 Eres responsable de revisar el contenido para tu hijo o familia.'
        ]
      },
      {
        title: '5A. Aviso y consentimiento sobre IA',
        content: [
          'Antes de que un usuario use por primera vez las funciones basadas en IA, la aplicación muestra un aviso en la app que explica qué datos pueden enviarse a proveedores externos de IA y con qué finalidad.',
          'Al tocar «Guardar» y continuar usando las funciones basadas en IA, el usuario acepta el tratamiento descrito en ese aviso.'
        ]
      },
      {
        title: '6. Niños y uso familiar',
        content: [
          'Nimny está diseñado para familias.',
          '• Los niños deben usar el Servicio con supervisión parental\n• No recopilamos conscientemente información personal de niños menores de 13 años sin consentimiento parental\n• Proporcionar datos relacionados con niños es opcional\n• Puedes usar apodos en lugar de nombres reales',
          'Si crees que un niño ha proporcionado datos personales sin consentimiento, contáctanos.'
        ]
      },
      {
        title: '7. Retención de datos',
        content: [
          'Retenemos información solo el tiempo necesario para:\n• proporcionar el Servicio\n• mantener la seguridad\n• cumplir con obligaciones legales',
          'Normalmente retenemos datos mientras tu cuenta esté activa y por un período limitado después.',
          'Puedes eliminar tu cuenta directamente en la aplicación yendo a Cuenta > Eliminar cuenta. También puedes contactarnos a través del formulario de contacto en nuestro sitio web si necesitas asistencia adicional relacionada con la privacidad.'
        ]
      },
      {
        title: '8. Tus derechos',
        content: [
          'Dependiendo de tu ubicación, puedes tener derechos a:\n• Acceder a tus datos\n• Corregir tus datos\n• Eliminar tus datos\n• Restringir el procesamiento\n• Oponerte al procesamiento',
          'Para ejercer tus derechos, puedes usar la función Eliminar cuenta en la aplicación o contactarnos a través del formulario de contacto en nuestro sitio web.'
        ]
      },
      {
        title: '9. Tus opciones',
        content: 'Puedes:\n• Desactivar permisos de micrófono\n• Editar o eliminar datos del perfil\n• Cancelar suscripción a emails\n• Controlar cookies a través de tu navegador'
      },
      {
        title: '10. Seguridad',
        content: 'Implementamos medidas técnicas y organizativas razonables. Sin embargo, ningún sistema es completamente seguro.'
      },
      {
        title: '11. Usuarios internacionales',
        content: 'Los datos pueden procesarse y almacenarse en Estados Unidos u otros países.'
      },
      {
        title: '12. Base legal (EEE/Reino Unido)',
        content: 'Procesamos datos basándonos en:\n• Contrato (proporcionar el Servicio)\n• Intereses legítimos (seguridad, mejora)\n• Consentimiento (funciones de voz, marketing)\n• Obligaciones legales'
      },
      {
        title: '13. Toma de decisiones automatizada',
        content: 'No usamos tus datos para tomar decisiones automatizadas que tengan efectos legales o similarmente significativos.'
      },
      {
        title: '14. Derechos de privacidad de California',
        content: [
          'Los residentes de California pueden tener derechos a:\n• Saber qué datos se recopilan\n• Solicitar eliminación\n• Solicitar corrección',
          'No vendemos información personal.'
        ]
      },
      {
        title: '15. Cambios a esta política',
        content: 'Podemos actualizar esta Política de Privacidad. Notificaremos a los usuarios sobre cambios significativos.'
      },
      {
        title: '16. Contacto',
        content: 'Para preguntas relacionadas con la privacidad, utiliza el formulario a continuación.'
      }
    ]
  }
};

const PrivacyPage = () => {
  const { language, setLanguage } = useLanguage();
  const location = useLocation();

  // Detect language from URL path
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/ru/')) {
      setLanguage('ru');
    } else if (path.startsWith('/es/')) {
      setLanguage('es');
    } else {
      setLanguage('en');
    }
  }, [location.pathname, setLanguage]);

  const content = privacyContent[language] || privacyContent.en;

  // Add canonical and hreflang tags
  useEffect(() => {
    const langCode = language === 'ru' ? 'ru' : language === 'es' ? 'es' : 'en';
    const canonicalUrl = language === 'en' ? 'https://readnimny.com/privacy' : `https://readnimny.com/${langCode}/privacy`;
    
    document.title = `${content.title} – Nimny`;

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    // Hreflang tags
    const hreflangData = [
      { lang: 'en', href: 'https://readnimny.com/privacy' },
      { lang: 'es', href: 'https://readnimny.com/es/privacy' },
      { lang: 'ru', href: 'https://readnimny.com/ru/privacy' },
      { lang: 'x-default', href: 'https://readnimny.com/privacy' }
    ];

    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    hreflangData.forEach(({ lang, href }) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang;
      link.href = href;
      document.head.appendChild(link);
    });

    return () => {
      document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    };
  }, [language, content.title]);

  const renderContent = (contentItem) => {
    if (Array.isArray(contentItem)) {
      return contentItem.map((paragraph, i) => (
        <p key={i} className="mb-3 whitespace-pre-line">{paragraph}</p>
      ));
    }
    return <p className="whitespace-pre-line">{contentItem}</p>;
  };

  return (
    <div className="min-h-screen relative">
      {/* Night sky background */}
      <NightAmbience />
      
      <header className="relative z-10 py-6 px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-200/70 hover:text-amber-200 transition-colors font-sans" data-testid="privacy-back-link">
            <ArrowLeft className="w-4 h-4" />
            {content.backToHome}
          </Link>
        </div>
      </header>

      <main className="relative z-10 px-6 pb-20">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-amber-200/30">
              <img src={NIMNY_AVATAR} alt="Nimny" className="w-full h-full object-cover" style={{ transform: 'scale(1.2)', objectPosition: 'center 35%' }} />
            </div>
            <span className="font-sans text-2xl text-[#f5ebe0]/90">Nimny</span>
          </div>

          <h1 className="font-sans text-3xl md:text-4xl text-[#f5ebe0]/90 text-center mb-4" data-testid="privacy-title">
            {content.title}
          </h1>

          <p className="text-amber-200/50 text-sm text-center mb-10 font-sans">
            {content.lastUpdated}
          </p>

          <div className="bg-[#f5ebe0]/5 backdrop-blur-sm rounded-2xl p-6 md:p-10 border border-amber-200/15">
            <p className="font-serif text-amber-200/70 leading-relaxed text-sm md:text-base mb-4">
              {content.intro}
            </p>
            
            <p className="font-serif text-amber-200/70 leading-relaxed text-sm md:text-base mb-8 italic">
              {content.disclaimer}
            </p>

            <div className="space-y-6 font-serif text-amber-200/70 leading-relaxed text-sm md:text-base">
              {content.sections.map((section, idx) => (
                <section key={idx}>
                  <h2 className="font-sans text-lg text-[#f5ebe0]/85 mb-3">{section.title}</h2>
                  {renderContent(section.content)}
                </section>
              ))}
            </div>

            {/* Support CTA — replaces the legacy email and routes users to the contact form */}
            <div className="mt-10 flex justify-center">
              <Link
                to={language === 'ru' ? '/ru/contact' : language === 'es' ? '/es/contact' : '/contact'}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#b8956b] hover:bg-[#a8855b] text-white font-sans transition-colors"
                data-testid="privacy-support-button"
              >
                {content.supportButton}
              </Link>
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-10 py-8 px-6 border-t border-amber-200/10">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-amber-200/30 font-sans text-xs">© 2026 Nimny LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
